import { AxiosError } from 'axios';

import { FormErrors } from './types';

function isResponseStatus(error: AxiosError, status: number): boolean {
  try {
    return !!error.response && error.response.status === status;
  } catch (e) {
    return false;
  }
}

export function isForbidden(error: AxiosError): boolean {
  return isResponseStatus(error, 403);
}

export function isUnauthorized(error: AxiosError): boolean {
  return isResponseStatus(error, 401);
}

export enum SessionErrorCode {
  RefreshRequired = 'session_refresh_required',
  Expired = 'session_expired',
  RefreshUnavailable = 'session_refresh_unavailable',
}

export function errorCode(error: AxiosError): string | undefined {
  const data = error.response?.data as { code?: unknown } | undefined;
  return typeof data?.code === 'string' ? data.code : undefined;
}

export function isBadRequest(error: AxiosError): boolean {
  return isResponseStatus(error, 400);
}

export function isNotFound(error: AxiosError): boolean {
  return isResponseStatus(error, 404);
}

export function isConflict(error: AxiosError): boolean {
  return isResponseStatus(error, 409);
}

interface InternalErrorResponse {
  'internal error'?: {
    response?: Record<string, unknown>;
  };
}

function extractInternalErrorData(data: Record<string, unknown>): Record<string, unknown> {
  const internalError = (data as InternalErrorResponse)['internal error'];
  if (internalError?.response && typeof internalError.response === 'object') {
    return internalError.response;
  }
  return data;
}

export function formErrors(error: AxiosError): FormErrors {
  const processErrorArray = (array: Record<string, string[]>[]): string[] => [
    ...new Set(array.flatMap((err) => Object.values(err).toString())),
  ];

  if (!error.response || !isBadRequest(error)) {
    return { nonFieldErrors: ['Something went wrong, please try later.'] };
  }

  const rawData = error.response.data as Record<string, unknown>;
  const data = extractInternalErrorData(rawData);

  if (Array.isArray(data)) {
    let values: string[];
    if (data.every((e) => typeof e === 'object')) values = processErrorArray(data);
    else values = data;

    return { nonFieldErrors: values };
  }
  const { nonFieldErrors, ...fieldErrors } = data;
  return {
    nonFieldErrors: nonFieldErrors as string[] | undefined,
    fieldErrors: fieldErrors as { [k: string]: string[] },
  };
}

export function apiErrors(error: AxiosError, defaultMessage: string): string[] {
  if (!error.response || !isBadRequest(error)) {
    return [defaultMessage];
  }

  return Object.values(error.response.data as Record<string, string>).flat();
}

function flattenErrorValue(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.length > 0 ? flattenErrorValue(value[0]) : '';
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${flattenErrorValue(v)}`)
      .join(', ');
  }
  return String(value);
}

export function apiErrorsWithKeys(error: AxiosError, defaultMessage: string): string {
  if (!error.response || !isBadRequest(error)) {
    return defaultMessage;
  }
  const errors = error.response.data as Record<string, unknown>;

  return Object.entries(errors)
    .map(([key, value]) => `${key}: ${flattenErrorValue(value)}`)
    .join(' | ');
}

export function getErrorMessage(error: AxiosError, defaultMessage: string): string {
  const data = error.response?.data;

  // Ninja returns { detail: "..." } for 4xx (409, 422, etc.)
  if (data && typeof data === 'object' && !Array.isArray(data) && 'detail' in data) {
    return (data as { detail: string }).detail || defaultMessage;
  }

  // formErrors only produces meaningful output for 400; for any other status it
  // returns a generic "Something went wrong..." that would shadow the caller's default.
  if (!isBadRequest(error)) {
    return defaultMessage;
  }

  const { nonFieldErrors, fieldErrors } = formErrors(error);

  if (nonFieldErrors && nonFieldErrors.length > 0) {
    return nonFieldErrors[0];
  }

  if (fieldErrors) {
    const messages = Object.values(fieldErrors).flat();
    if (messages.length > 0) {
      return messages[0] as string;
    }
  }

  return defaultMessage;
}

export function fabricateFormErrorResponse(errorData: {
  fieldErrors?: string[];
  nonFieldErrors?: string[];
}): AxiosError<unknown> {
  return {
    response: {
      status: 400,
      data: errorData,
    },
  } as unknown as AxiosError;
}
